import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  FileText, Shield, CheckCircle2, ArrowRight, 
  Users, Clock, Target, Sparkles,
  FileCheck, BarChart3, Video, UserCheck, AlertTriangle
} from "lucide-react";
import { TrustNavigation } from "@/components/TrustNavigation";
import { TrustFooter } from "@/components/TrustFooter";

export default function Methodology() {

  const assessmentSteps = [
    {
      step: 1,
      title: "Application Receipt & Initial Evaluation",
      description: "Applications are received and undergo automated consistency checks. Basic eligibility criteria are verified against role requirements.",
      outputs: ["Eligibility confirmation", "Application timestamp", "Initial data validation"]
    },
    {
      step: 2,
      title: "Structured Assessment Administration",
      description: "Candidates complete a structured video assessment with role-specific questions. Responses are recorded without retakes to capture genuine, spontaneous answers.",
      outputs: ["Video recordings", "Response timestamps", "Technical quality metrics"]
    },
    {
      step: 3,
      title: "Credential Verification",
      description: "Claimed qualifications, certifications, and experience are verified through available documentation and third-party credential sources.",
      outputs: ["Verification status report", "Credential confidence scores", "Discrepancy flags"]
    },
    {
      step: 4,
      title: "Consistency Analysis",
      description: "Written application materials are compared against verbal assessment responses to identify alignment and potential inconsistencies.",
      outputs: ["Consistency score", "Alignment indicators", "Areas requiring agent review"]
    },
    {
      step: 5,
      title: "Agent Review & Curation",
      description: "A dedicated agent reviews all assessment outputs, applies professional judgment, and prepares the candidate profile for employer visibility.",
      outputs: ["Agent notes", "Recommendation status", "Curated dossier"]
    },
    {
      step: 6,
      title: "Dossier Delivery",
      description: "Finalized candidate dossiers are delivered to employers via secure share links with appropriate visibility controls based on tier.",
      outputs: ["Secure share link", "Tier-appropriate data package", "Delivery confirmation"]
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#05070a", color: "#ffffff" }}>
      <TrustNavigation currentPath="/methodology" />

      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950" />
        <div className="container mx-auto px-4 py-10 md:py-24 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium border border-white/20 mb-6">
              <FileText className="w-4 h-4" />
              <span>Assessment Methodology</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              How We Assess Candidates
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              A structured, documented process designed for consistency, auditability, and bias reduction.
            </p>
            <p className="text-sm text-slate-400 mt-4">
              Last updated: January 2026 | Version 2.0
            </p>
          </div>
        </div>
      </section>

      <section className="py-16" style={{ backgroundColor: "#05070a" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 bg-slate-800/20 border-white/10 mb-12 shadow-sm">
              <div className="flex items-start gap-4">
                <Shield className="w-8 h-8 text-slate-300 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-white text-lg mb-3">Methodology Principles</h3>
                  <ul className="space-y-3 text-slate-300">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Structured Evaluation:</strong> All candidates are assessed using the same criteria for their role type.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Evidence-Based:</strong> Assessments are based on documented responses, not subjective impressions.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Agent Oversight:</strong> Technology assists but agents make final curation decisions.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Auditability:</strong> Each step produces documented outputs that can be reviewed.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>

            <h2 className="text-2xl font-bold text-white mb-8">Assessment Process Steps</h2>
            
            <div className="space-y-6">
              {assessmentSteps.map((step) => (
                <Card key={step.step} className="p-6 bg-slate-800/30 border-white/10">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{step.step}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white text-lg mb-2">{step.title}</h3>
                      <p className="text-slate-400 mb-4">{step.description}</p>
                      <div className="bg-slate-800/20 rounded-lg p-4">
                        <p className="text-sm font-medium text-slate-300 mb-2">Documented Outputs:</p>
                        <ul className="flex flex-wrap gap-2">
                          {step.outputs.map((output, idx) => (
                            <li key={idx} className="text-xs px-3 py-1 rounded-full" style={{ color: "#e2e8f0", background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.3)" }}>
                              {output}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16" style={{ backgroundColor: "#05070a" }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">Service Scope & Limitations</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6 bg-slate-800/30 border-white/10">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  What This Methodology Covers
                </h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>Structured video assessment administration</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>Credential verification from available sources</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>Consistency analysis between materials</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>Agent-curated candidate profiles</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>Tiered visibility for employer access</li>
                </ul>
              </Card>
              <Card className="p-6 bg-slate-800/30 border-white/10">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Limitations
                </h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>Not a guarantee of candidate performance</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>Background checks are not included unless specified</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>Reference verification requires separate arrangement</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>Final hiring decisions rest with the employer</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>Assessment accuracy depends on candidate disclosure</li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <TrustFooter />
    </div>
  );
}
